/**
 * Brazilian CPF utilities. Mirrors the backend CpfValidator algorithm so the
 * client can give instant feedback before hitting the server.
 *
 * IMPORTANT: keep this in sync with FillSign.Infrastructure/Helpers/CpfValidator.cs
 */

/**
 * Strips a CPF of all non-digit characters.
 */
export function normalizeCpf(value: string): string {
  return (value || "").replace(/\D/g, "");
}

/**
 * Formats a partial or complete CPF as the user types.
 * Examples: "123" -> "123", "12345" -> "123.45", "12345678900" -> "123.456.789-00"
 */
export function formatCpf(value: string): string {
  const cleaned = normalizeCpf(value).slice(0, 11);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 9) {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  }
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
}

/**
 * Validates the two CPF check digits using the official Receita Federal algorithm.
 * Returns false for: empty, wrong length, all-equal-digit sequences, or failed checksum.
 *
 * This is the same algorithm as the backend CpfValidator.IsValid.
 */
export function isCpfChecksumValid(value: string): boolean {
  const cleaned = normalizeCpf(value);
  if (cleaned.length !== 11) return false;

  // Reject sequences like "00000000000", "11111111111" - they pass math but are invalid.
  if (/^(\d)\1+$/.test(cleaned)) return false;

  const digits = cleaned.split("").map(Number);

  // First check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += digits[i] * (10 - i);
  let remainder = sum % 11;
  const checkDigit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digits[9] !== checkDigit1) return false;

  // Second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) sum += digits[i] * (11 - i);
  remainder = sum % 11;
  const checkDigit2 = remainder < 2 ? 0 : 11 - remainder;
  if (digits[10] !== checkDigit2) return false;

  return true;
}

/**
 * UI status of the CPF input. Used to drive visual feedback in the form.
 *
 * - idle: empty or partial input
 * - checking: client passed checksum, server check is in flight
 * - valid: server confirmed CPF is valid AND not in use
 * - invalid_checksum: failed the algorithmic check
 * - already_in_use: CPF is already linked to another account
 * - error: network or unexpected error during the server check
 */
export type CpfStatus =
  | "idle"
  | "checking"
  | "valid"
  | "invalid_checksum"
  | "already_in_use"
  | "error";
